import { useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

// Someone mid-message (a text box has focus): the 24-hour sign-out waits for
// the next check rather than throw away what they are typing (owner, 3 Nov
// 2026: "it won't refresh and lose people's text from messages, for example in
// the middle before send?"). It waits at most an hour past the limit.
const GRACE_MS = 60 * 60 * 1000;
export const isTyping = (el = typeof document !== 'undefined' ? document.activeElement : null) =>
  !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || ''));

const useSessionEnforcer = (isEnabled = true) => {
  useEffect(() => {
    if (!isEnabled) return;

    const SESSION_LIMIT = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const SESSION_KEY = 'parcelink_session_started_at';

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        localStorage.removeItem(SESSION_KEY);
        return;
      }

      const startedAt = localStorage.getItem(SESSION_KEY);
      const now = Date.now();

      if (!startedAt) {
        // First time seeing a session, mark it
        localStorage.setItem(SESSION_KEY, now.toString());
        return;
      }

      const age = now - parseInt(startedAt);
      if (age > SESSION_LIMIT) {
        if (isTyping() && age < SESSION_LIMIT + GRACE_MS) return; // ask again at the next check
        // Session expired
        console.warn("Session expired (24h limit). Logging out...");
        localStorage.removeItem(SESSION_KEY);
        try { await supabase.auth.signOut({ scope: 'local' }); } catch (_e) { /* already gone */ }
        window.location.href = '/'; // Force redirect to login
      }
    };

    // Check on mount
    checkSession();

    // Check on tab focus / visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Low-frequency heartbeat (every 5 minutes)
    const interval = setInterval(checkSession, 5 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [isEnabled]);
};

export default useSessionEnforcer;
